import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customerAPI, careReportAPI, packageAPI } from '../api/axios';
import Button from '../components/Button';
import {
  ArrowLeft,
  Save,
  User,
  Phone,
  Briefcase,
  MapPin,
  Heart,
  Baby,
  Droplets,
  AlertTriangle,
  FileText,
  Tag,
  X,
  Plus,
  Clock,
  Calendar,
  CheckCircle,
  Edit3,
  CreditCard
} from 'lucide-react';
import { formatNumber } from '../utils/dateUtils';
import './CustomerDetailPage.css';

/**
 * 고객 상세 / 수정 페이지
 * - 기본 정보, 피부/건강 정보, 메모, 태그 관리
 * - 케어 리포트 히스토리 요약
 */
const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // 상태 관리
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [careHistory, setCareHistory] = useState([]);
  const [activePackages, setActivePackages] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  // 폼 데이터
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    skinType: '',
    allergy: '',
    memo: '',
    job: '',
    address: '',
    isMarried: false,
    isPregnant: false,
    tags: '',
  });

  // 태그 입력
  const [tagInput, setTagInput] = useState('');
  const [tagList, setTagList] = useState([]);

  // 피부 타입 옵션
  const skinTypeOptions = [
    { value: '', label: '선택 안함' },
    { value: '건성', label: '건성' },
    { value: '지성', label: '지성' },
    { value: '복합성', label: '복합성' },
    { value: '민감성', label: '민감성' },
    { value: '중성', label: '중성' },
    { value: '건성+민감', label: '건성+민감' },
    { value: '지성+민감', label: '지성+민감' },
  ];

  // 자주 쓰는 태그 프리셋
  const tagPresets = ['VIP', '민감피부', '알러지주의', '웨딩준비', '임산부', '직장인', '재방문유도', '신규고객', '노쇼이력', '단골'];

  // 데이터 로드
  useEffect(() => {
    fetchCustomerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      
      // 고객 정보 조회
      const customerRes = await customerAPI.getById(id);
      const customerData = customerRes.data;
      setCustomer(customerData);

      // 폼 데이터 세팅
      setFormData({
        name: customerData.name || '',
        phone: customerData.phone || '',
        skinType: customerData.skinType || '',
        allergy: customerData.allergy || '',
        memo: customerData.memo || '',
        job: customerData.job || '',
        address: customerData.address || '',
        isMarried: customerData.isMarried || false,
        isPregnant: customerData.isPregnant || false,
        tags: customerData.tags || '',
      });

      // 태그 파싱
      if (customerData.tags) {
        setTagList(customerData.tags.split(',').filter(t => t.trim()));
      } else {
        setTagList([]);
      }

      // 케어 히스토리 조회
      try {
        const careRes = await careReportAPI.getAll(id);
        setCareHistory(careRes.data);
      } catch (careErr) {
        console.log('케어 히스토리 없음');
        setCareHistory([]);
      }

      // 활성 패키지 조회
      try {
        const packagesRes = await packageAPI.getActive(id);
        setActivePackages(packagesRes.data);
      } catch (pkgErr) {
        console.log('활성 패키지 없음');
        setActivePackages([]);
      }

      setError(null);
    } catch (err) {
      console.error('고객 정보 로드 실패:', err);
      setError('고객 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 입력값 변경
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // 태그 추가
  const handleAddTag = (tag) => {
    const trimmed = tag.trim();
    if (!trimmed) return;
    if (tagList.includes(trimmed)) return;
    
    const newTags = [...tagList, trimmed];
    setTagList(newTags);
    setFormData(prev => ({ ...prev, tags: newTags.join(',') }));
    setTagInput('');
  };

  // 태그 입력 엔터 처리
  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag(tagInput);
    }
  };

  // 태그 삭제
  const handleRemoveTag = (tagToRemove) => {
    const newTags = tagList.filter(t => t !== tagToRemove);
    setTagList(newTags);
    setFormData(prev => ({ ...prev, tags: newTags.join(',') }));
  };

  // 저장
  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage('');

    try {
      await customerAPI.update(id, formData);
      setSuccessMessage('고객 정보가 저장되었습니다.');
      setIsEditing(false);
      
      // 최신 데이터 다시 로드
      await fetchCustomerData();

      // 3초 후 메시지 제거
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('저장 실패:', err);
      alert('저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  // 날짜 포맷
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit'
    });
  };

  // D-day 계산
  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // 로딩
  if (loading) {
    return (
      <div className="customer-detail-page">
        <div className="detail-loading">
          <div className="detail-spinner"></div>
          <p>고객 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // 에러
  if (error) {
    return (
      <div className="customer-detail-page">
        <div className="detail-error">
          <p>{error}</p>
          <Button onClick={() => navigate('/admin/customers')} variant="outline" size="medium">
            목록으로 돌아가기
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-detail-page">
      {/* 상단 네비게이션 */}
      <div className="detail-nav">
        <Button variant="outline" size="small" onClick={() => navigate('/admin/customers')}>
          <ArrowLeft size={18} />
          <span>고객 목록</span>
        </Button>
        <div className="nav-actions">
          {!isEditing ? (
            <Button variant="outline" size="small" onClick={() => setIsEditing(true)}>
              <Edit3 size={16} />
              <span>편집</span>
            </Button>
          ) : (
            <>
              <Button variant="outline" size="small" onClick={() => {
                setIsEditing(false);
                fetchCustomerData(); // 원래 데이터로 복원
              }}>
                취소
              </Button>
              <Button variant="primary" size="small" onClick={handleSave} disabled={saving} loading={saving}>
                <Save size={16} />
                <span>저장</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* 성공 메시지 */}
      {successMessage && (
        <div className="success-toast">
          <CheckCircle size={18} />
          <span>{successMessage}</span>
        </div>
      )}

      {/* 프로필 헤더 카드 */}
      <div className="profile-header-card">
        <div className="profile-avatar">
          <span>{customer?.name?.charAt(0) || '?'}</span>
        </div>
        <div className="profile-info">
          <h2 className="profile-name">{customer?.name}</h2>
          <p className="profile-phone">{customer?.phone}</p>
          <div className="profile-badges">
            <span className="badge badge-visit">
              <Clock size={12} />
              방문 {customer?.visitCount || 0}회
            </span>
            {customer?.noShowCount > 0 && (
              <span className="badge badge-noshow">
                노쇼 {customer.noShowCount}회
              </span>
            )}
            <span className="badge badge-date">
              <Calendar size={12} />
              가입 {formatDate(customer?.createdAt)}
            </span>
          </div>
        </div>
        {/* 태그 표시 (읽기 모드) */}
        {tagList.length > 0 && (
          <div className="profile-tags">
            {tagList.map((tag, i) => (
              <span key={i} className="tag-chip">
                <Tag size={11} />
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 메인 컨텐츠 그리드 */}
      <div className="detail-grid">
        {/* 좌측: 정보 편집 */}
        <div className="detail-left">
          
          {/* 기본 정보 섹션 */}
          <div className="detail-section">
            <h3 className="section-title">
              <User size={18} />
              기본 정보
            </h3>
            <div className="form-grid">
              <div className="form-group">
                <label>이름</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="고객 이름"
                />
              </div>
              <div className="form-group">
                <label><Phone size={14} /> 연락처</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="010-0000-0000"
                />
              </div>
              <div className="form-group">
                <label><Briefcase size={14} /> 직업</label>
                <input
                  type="text"
                  name="job"
                  value={formData.job}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="예: 회사원, 프리랜서"
                />
              </div>
              <div className="form-group">
                <label><MapPin size={14} /> 주소</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="예: 강남구 논현동"
                />
              </div>
            </div>
          </div>

          {/* 피부/건강 정보 섹션 */}
          <div className="detail-section">
            <h3 className="section-title">
              <Droplets size={18} />
              피부 · 건강 정보
            </h3>
            <div className="form-grid">
              <div className="form-group">
                <label>피부 타입</label>
                <select
                  name="skinType"
                  value={formData.skinType}
                  onChange={handleChange}
                  disabled={!isEditing}
                >
                  {skinTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>
                  <AlertTriangle size={14} />
                  알러지 정보
                </label>
                <input
                  type="text"
                  name="allergy"
                  value={formData.allergy}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="예: 니켈 알러지, 특정 화장품 성분"
                  className={formData.allergy ? 'has-allergy' : ''}
                />
              </div>
            </div>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isMarried"
                  checked={formData.isMarried}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <Heart size={14} />
                <span>기혼</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isPregnant"
                  checked={formData.isPregnant}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <Baby size={14} />
                <span>임산부 (기기 관리 주의)</span>
                {formData.isPregnant && (
                  <span className="caution-badge">⚠️ 주의</span>
                )}
              </label>
            </div>
          </div>

          {/* 태그 관리 섹션 */}
          <div className="detail-section">
            <h3 className="section-title">
              <Tag size={18} />
              태그 관리
            </h3>
            
            {/* 현재 태그 */}
            <div className="tags-display">
              {tagList.length === 0 && !isEditing && (
                <p className="empty-text">등록된 태그가 없습니다.</p>
              )}
              {tagList.map((tag, i) => (
                <span key={i} className="tag-chip editable">
                  {tag}
                  {isEditing && (
                    <button 
                      className="tag-remove" 
                      onClick={() => handleRemoveTag(tag)}
                      type="button"
                    >
                      <X size={12} />
                    </button>
                  )}
                </span>
              ))}
            </div>

            {/* 태그 입력 (편집 모드) */}
            {isEditing && (
              <>
                <div className="tag-input-row">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder="새 태그 입력 후 Enter"
                    className="tag-input"
                  />
                  <button 
                    className="tag-add-btn"
                    onClick={() => handleAddTag(tagInput)}
                    type="button"
                  >
                    <Plus size={16} />
                  </button>
                </div>
                {/* 프리셋 태그 */}
                <div className="tag-presets">
                  <span className="preset-label">추천 태그:</span>
                  {tagPresets
                    .filter(p => !tagList.includes(p))
                    .map((preset, i) => (
                      <button
                        key={i}
                        className="tag-preset-btn"
                        onClick={() => handleAddTag(preset)}
                        type="button"
                      >
                        + {preset}
                      </button>
                    ))
                  }
                </div>
              </>
            )}
          </div>
        </div>

        {/* 우측: 메모 + 케어 히스토리 */}
        <div className="detail-right">
          
          {/* 원장님 메모 섹션 */}
          <div className="detail-section memo-section">
            <h3 className="section-title">
              <FileText size={18} />
              원장님 메모
              <span className="memo-hint">비공개</span>
            </h3>
            <textarea
              name="memo"
              value={formData.memo}
              onChange={handleChange}
              disabled={!isEditing}
              placeholder={isEditing 
                ? "이 고객에 대한 특이사항, 선호하는 시술, 대화 중 기억할 내용 등을 자유롭게 메모하세요...\n\n예:\n- 등 여드름 고민 있음\n- 시술 후 진정팩 선호\n- 오후 시간대 선호\n- 생일: 3/15" 
                : "아직 작성된 메모가 없습니다."
              }
              rows={10}
              className="memo-textarea"
            />
          </div>

          {/* 보유 패키지 섹션 */}
          <div className="detail-section">
            <div className="section-title-row">
              <h3 className="section-title">
                <CreditCard size={18} />
                보유 패키지
              </h3>
              <Button
                variant="outline"
                size="small"
                onClick={() => navigate('/admin/packages')}
              >
                전체보기 →
              </Button>
            </div>

            {activePackages.length === 0 ? (
              <div className="empty-package">
                <CreditCard size={36} className="empty-icon" />
                <p>보유 중인 패키지가 없습니다.</p>
              </div>
            ) : (
              <div className="package-list">
                {activePackages.map((pkg) => {
                  const daysLeft = getDaysUntilExpiry(pkg.expiryDate);
                  return (
                    <div key={pkg.id} className="package-card-mini">
                      <div className="package-card-header">
                        <span className="package-name">{pkg.packageName}</span>
                        <span className={`package-type-badge ${pkg.packageType}`}>
                          {pkg.packageType === 'COUNT_BASED' ? '횟수권' : '정액권'}
                        </span>
                      </div>
                      <div className="package-remaining">
                        {pkg.packageType === 'COUNT_BASED' ? (
                          <span className="remaining-text">
                            <strong>{pkg.remainingCount}</strong> / {pkg.totalCount}회 남음
                          </span>
                        ) : (
                          <span className="remaining-text">
                            <strong>{formatNumber(pkg.remainingAmount)}</strong> / {formatNumber(pkg.totalAmount)}원 남음
                          </span>
                        )}
                      </div>
                      <div className="package-expiry">
                        만료: {formatDate(pkg.expiryDate)}
                        {daysLeft !== null && (
                          <span className={`expiry-badge ${daysLeft <= 7 ? 'urgent' : ''}`}>
                            D-{daysLeft}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 케어 히스토리 요약 */}
          <div className="detail-section">
            <div className="section-title-row">
              <h3 className="section-title">
                <Clock size={18} />
                최근 관리 기록
              </h3>
              {careHistory.length > 0 && (
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => navigate('/admin/care-reports')}
                >
                  전체보기 →
                </Button>
              )}
            </div>

            {careHistory.length === 0 ? (
              <div className="empty-care">
                <FileText size={36} className="empty-icon" />
                <p>아직 관리 기록이 없습니다.</p>
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => navigate('/admin/care-reports')}
                >
                  케어 리포트 작성하러 가기
                </Button>
              </div>
            ) : (
              <div className="care-history-list">
                {careHistory.slice(0, 5).map((report) => (
                  <div key={report.id} className="care-history-item">
                    <div className="care-date">
                      <Calendar size={13} />
                      {formatDate(report.createdAt)}
                    </div>
                    <div className="care-content">
                      {report.content.length > 80 
                        ? report.content.substring(0, 80) + '...' 
                        : report.content
                      }
                    </div>
                    <div className="care-admin">담당: {report.adminName}</div>
                  </div>
                ))}
                {careHistory.length > 5 && (
                  <p className="more-records">
                    +{careHistory.length - 5}건의 기록이 더 있습니다.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailPage;
